import { toast } from "@suraasa/placebo-ui"
import api from "api"
import { PaymentMethodType } from "api/resources/payment/types"
import { AuthManager } from "features/Authentication/AuthManager"
import { useNavigate } from "react-router-dom"
import routes from "routes"
import {
  buildUserName,
  isFreeOrderResponse,
  mapErrorsV2,
  sleep,
} from "utils/helpers"
import useFormatPrice from "utils/hooks/useFormatPrice"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

import { init as initRazorpay } from "./payment/razorpay"
import { init as initStripe } from "./payment/stripe"
import useScript from "./useScript"

export enum PaymentTheme {
  ITU = "itu",
  SURAASA = "suraasa",
  CPAT = "cpat",
}

type Props = {
  stripeConfig?: {
    theme?: PaymentTheme
    logo?: () => JSX.Element
    logoSrc?: string
    getForm: (form: JSX.Element) => void
    handleClose: () => void
    redirectURL: string
    paymentKey: string
    onClientSecretGenerated?: (secret: string) => void
  }
  razorpayConfig?: {
    logo: string
    successHandler: () => void
    getUserDetails?: () => {
      name?: string
      email?: string
      contact?: string
    }
  }
  juspayConfig?: {
    setJuspayPaymentURL: (value: string) => void
  }
  purchaseDetails: {
    itemName: string
    currency: {
      code: string
      symbol: string
    }
    amount: number
  } | null
  onOrderCreated?: () => void
  // Useful for cases like free order, where a payment gateway is not involved
  successURL?: string
  userlessOrderToken?: string | null
}

type Options = {
  promocode?: string | null
  fallback?: boolean
  userlessOrderToken?: string | null
}

export const usePayment = ({
  purchaseDetails,
  stripeConfig,
  razorpayConfig,
  juspayConfig,
  onOrderCreated,
  successURL,
  ...props
}: Props) => {
  const navigate = useNavigate()
  const formatPrice = useFormatPrice()
  const rzpScriptStatus = useScript(
    "https://checkout.razorpay.com/v1/checkout.js"
  )

  const changePaymentPlan = async ({
    paymentMethod,
    mandateId,
  }: {
    paymentMethod: PaymentMethodType
    mandateId: string
  }) => {
    if (paymentMethod === PaymentMethodType.razorpaySubscription) {
      if (!razorpayConfig || !purchaseDetails)
        throw new Error(
          `Razorpay config or purchaseDetails not found - razorpayConfig: ${razorpayConfig}, purchaseDetails: ${purchaseDetails}`
        )

      try {
        const authInfo = AuthManager.getPlatformAuth()

        initRazorpay({
          currency: purchaseDetails.currency.code,
          subscription_id: mandateId,
          subscription_card_change: false,
          name: purchaseDetails.itemName,
          image: razorpayConfig.logo,
          prefill: {
            name: authInfo
              ? [authInfo.user.firstName, authInfo.user.lastName]
                  .filter(Boolean)
                  .join(" ")
              : "",
            email: authInfo?.user.email,
          },
          handler: () => {
            razorpayConfig.successHandler()
          },
        })
      } catch (e) {
        toast.error("Something went wrong, please try again later.")
        throw new Error(`Stripe initialization failed ${e}`)
      }
    }
  }

  const updateSubscriptionPaymentMethod = async ({
    paymentMethod,
    mandateId,
  }: {
    paymentMethod: PaymentMethodType
    mandateId: string
  }) => {
    if (paymentMethod === PaymentMethodType.razorpaySubscription) {
      if (!razorpayConfig || !purchaseDetails)
        throw new Error(
          `Razorpay config or purchaseDetails not found - razorpayConfig: ${razorpayConfig}, purchaseDetails: ${purchaseDetails}`
        )

      try {
        const authInfo = AuthManager.getPlatformAuth()
        initRazorpay({
          currency: purchaseDetails.currency.code,
          subscription_id: mandateId,
          subscription_card_change: true,
          name: purchaseDetails.itemName,
          image: razorpayConfig.logo,
          prefill: {
            name: authInfo
              ? [authInfo.user.firstName, authInfo.user.lastName]
                  .filter(Boolean)
                  .join(" ")
              : "",
            email: authInfo?.user.email,
          },
          handler: () => {
            razorpayConfig.successHandler()
          },
        })
      } catch (e) {
        toast.error("Something went wrong, please try again later.")
        throw new Error(`Razorpay initialization failed ${e}`)
      }
    }
    if (paymentMethod === PaymentMethodType.stripeSubscriptionLink) {
      if (!stripeConfig || !purchaseDetails)
        throw new Error(
          `Stripe config or purchaseDetails not found - stripeConfig: ${stripeConfig}, purchaseDetails: ${purchaseDetails}`
        )

      try {
        const setupIntentResponse = await api.subscriptions.createSetupIntent()

        const redirectURL = new URL(stripeConfig.redirectURL)
        stripeConfig.redirectURL = redirectURL.toString()

        stripeConfig.getForm(
          initStripe({
            clientSecret: setupIntentResponse.clientSecret,
            itemName: purchaseDetails.itemName,
            price: `${purchaseDetails.currency.symbol}${purchaseDetails.amount}`,
            open: true,
            isUpdatePaymentMethod: true,
            ...stripeConfig,
          })
        )
      } catch (e) {
        toast.error("Something went wrong, please try again later.")
        throw new Error(`Stripe initialization failed ${e}`)
      }
    }
  }

  const fetchPaymentMethod = async (planId: string, options?: Options) => {
    if (!purchaseDetails) {
      throw new Error("Purchase details not found")
    }

    const userlessOrderToken =
      options?.userlessOrderToken || props.userlessOrderToken

    const promocode = options?.promocode || null

    try {
      const res = await (userlessOrderToken
        ? api.payment.userless.listPaymentMethod
        : api.payment.listPaymentMethod)({
        urlParams: { paymentPlanId: planId },
        data: userlessOrderToken
          ? {
              orderToken: userlessOrderToken,
            }
          : {},
        params: {
          callback_url: window.location.href,
          fallback: options?.fallback || false,
        },
      })

      trackingService.trackEvent(TrackingEvents.orderCheckout, {
        promocode,
      })

      if (isFreeOrderResponse(res)) {
        trackingService.trackEvent(TrackingEvents.purchase, {
          promocode,
          amount: purchaseDetails.amount,
          currency_code: purchaseDetails.currency.code,
          item_name: purchaseDetails.itemName,
        })

        // Await for 3 seconds to let backend process the item allocation before redirecting
        await sleep(3000)
        if (successURL) window.location.href = successURL
        else
          navigate({
            pathname: routes.orders,
            search: window.location.search,
          })
        return
      }

      const method = res[0]

      const { orderId, mandateLinkId } = method.paymentMethodDetails
      await onOrderCreated?.()

      if (method.paymentMethodId === PaymentMethodType.juspay && juspayConfig) {
        const { paymentLink } = method.paymentMethodDetails
        if (!paymentLink) throw new Error("Juspay payment link not found")
        juspayConfig.setJuspayPaymentURL(paymentLink)
      }
      if (
        method.paymentMethodId === PaymentMethodType.razorpay &&
        razorpayConfig
      ) {
        if (rzpScriptStatus !== "ready") {
          await fetchPaymentMethod(planId, {
            promocode,
            fallback: true,
          })
          return
        }

        try {
          const authInfo = AuthManager.getPlatformAuth()
          const userDetails = razorpayConfig.getUserDetails?.()

          initRazorpay({
            currency: purchaseDetails.currency.code,
            order_id: orderId,
            name: purchaseDetails.itemName,
            image: razorpayConfig.logo,
            prefill: {
              name:
                userDetails?.name ||
                (authInfo ? buildUserName(authInfo.user) : ""),
              email: userDetails?.email || authInfo?.user.email,
              contact: userDetails?.contact || undefined,
            },
            handler: () => {
              trackingService.trackEvent(TrackingEvents.purchase, {
                promocode,
                amount: purchaseDetails.amount,
                currency_code: purchaseDetails.currency.code,
                item_name: purchaseDetails.itemName,
              })
              razorpayConfig.successHandler()
            },
          })
        } catch (e) {
          console.error(e)
          console.warn("Using fallback payment system")
          await fetchPaymentMethod(planId, {
            promocode,
            fallback: true,
          })
          return
        }
      }

      if (method.paymentMethodId === PaymentMethodType.razorpayPaymentLink) {
        const { paymentLinkUrl } = method.paymentMethodDetails

        if (paymentLinkUrl) {
          window.location.href = paymentLinkUrl
        }
      }

      if (
        method.paymentMethodId === PaymentMethodType.razorpaySubscription &&
        razorpayConfig &&
        mandateLinkId
      ) {
        if (rzpScriptStatus !== "ready") {
          await fetchPaymentMethod(planId, {
            promocode,
            fallback: true,
          })
          return
        }

        try {
          const authInfo = AuthManager.getPlatformAuth()
          const userDetails = razorpayConfig.getUserDetails?.()
          initRazorpay({
            currency: purchaseDetails.currency.code,
            subscription_id: mandateLinkId,
            subscription_card_change: false,
            name: purchaseDetails.itemName,
            image: razorpayConfig.logo,
            prefill: {
              name:
                userDetails?.name ||
                (authInfo ? buildUserName(authInfo.user) : ""),
              email: userDetails?.email || authInfo?.user.email,
              contact: userDetails?.contact || undefined,
            },
            handler: () => {
              trackingService.trackEvent(TrackingEvents.purchase, {
                promocode,
                amount: purchaseDetails.amount,
                currency_code: purchaseDetails.currency.code,
                item_name: purchaseDetails.itemName,
              })
              razorpayConfig.successHandler()
            },
          })
        } catch (e) {
          console.error(e)
          console.warn("Using fallback payment system")
          await fetchPaymentMethod(planId, {
            promocode,
            fallback: true,
          })
          return
        }
      }

      if (method.paymentMethodId === PaymentMethodType.stripeSubscriptionLink) {
        const { mandateUrl } = method.paymentMethodDetails

        if (mandateUrl) {
          window.location.href = mandateUrl
        }
      }

      if (method.paymentMethodId === PaymentMethodType.stripe && stripeConfig) {
        const { clientSecret } = method.paymentMethodDetails
        if (!clientSecret) throw new Error("Stripe client secret not found")
        stripeConfig.onClientSecretGenerated?.(clientSecret)

        // Add payment plan id to redirect url
        const redirectURL = new URL(stripeConfig.redirectURL)

        // Merge Params
        const currentParams = new URLSearchParams(window.location.search)
        currentParams.forEach((value, key) => {
          redirectURL.searchParams.set(key, value)
        })
        redirectURL.searchParams.set("paymentPlanId", planId)
        redirectURL.searchParams.set("order_status", "success")

        stripeConfig.redirectURL = redirectURL.toString()

        stripeConfig.getForm(
          initStripe({
            clientSecret,
            itemName: purchaseDetails.itemName,
            price: `${purchaseDetails.currency.symbol}${formatPrice(
              purchaseDetails.amount
            )}`,
            open: true,
            ...stripeConfig,
          })
        )
      }

      return method.paymentMethodId
    } catch (e) {
      mapErrorsV2(e)
    }
  }

  return {
    checkout: fetchPaymentMethod,
    updateSubscriptionPaymentMethod,
    changePaymentPlan,
  }
}
